/**
 * ResearchBriefEditor — editable view of a Phase 1 brief. Users can
 * review AI-composed queries, tweak preferred/negative source lists, drop
 * or add queries, and save. Save PATCHes /brief/current and bumps
 * /brief/history/v{N+1} via the backend endpoint.
 *
 * Sprint 18 MVP — TextFields + chip-editors, no rich DnD reordering. We can
 * iterate the UX in later sprints once the end-to-end flow is validated.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Chip,
  Button,
  IconButton,
  Alert,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

export interface ResearchBriefEditorProps {
  brief: any;
  saving?: boolean;
  onSave: (patch: Record<string, unknown>) => Promise<void> | void;
}

export const ResearchBriefEditor: React.FC<ResearchBriefEditorProps> = ({
  brief,
  saving,
  onSave,
}) => {
  const [refinedDescription, setRefinedDescription] = useState<string>(
    brief?.refinedDescription ?? '',
  );
  const [queries, setQueries] = useState<any[]>(brief?.queries ?? []);
  const [preferredSources, setPreferredSources] = useState<string[]>(
    brief?.preferredSources ?? [],
  );
  const [negativeSources, setNegativeSources] = useState<string[]>(
    brief?.negativeSources ?? [],
  );
  const [newPreferred, setNewPreferred] = useState('');
  const [newNegative, setNewNegative] = useState('');

  useEffect(() => {
    setRefinedDescription(brief?.refinedDescription ?? '');
    setQueries(brief?.queries ?? []);
    setPreferredSources(brief?.preferredSources ?? []);
    setNegativeSources(brief?.negativeSources ?? []);
  }, [brief?.version, brief]);

  const dirty = useMemo(() => {
    return (
      refinedDescription !== (brief?.refinedDescription ?? '') ||
      JSON.stringify(queries) !== JSON.stringify(brief?.queries ?? []) ||
      JSON.stringify(preferredSources) !== JSON.stringify(brief?.preferredSources ?? []) ||
      JSON.stringify(negativeSources) !== JSON.stringify(brief?.negativeSources ?? [])
    );
  }, [brief, refinedDescription, queries, preferredSources, negativeSources]);

  const handleQueryChange = (idx: number, field: string, value: string) => {
    const next = queries.slice();
    next[idx] = { ...next[idx], [field]: value };
    setQueries(next);
  };

  const handleAddQuery = () => {
    setQueries([
      ...queries,
      {
        text: '',
        rationale: '',
        expectedSourceType: 'news',
      },
    ]);
  };

  const handleRemoveQuery = (idx: number) => {
    setQueries(queries.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    await onSave({
      refinedDescription,
      queries,
      preferredSources,
      negativeSources,
    });
  };

  return (
    <Stack spacing={2}>
      {brief?.editedByUser && (
        <Alert severity="info" variant="outlined">
          This brief has been edited — the latest version is {brief?.version ?? 'v?'}
        </Alert>
      )}

      <Box>
        <Typography variant="caption" color="text.secondary">
          Refined description
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          value={refinedDescription}
          onChange={(e) => setRefinedDescription(e.target.value)}
          data-testid="research-brief-description"
        />
      </Box>

      <Box>
        <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            Queries ({queries.length})
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={handleAddQuery}>
            Add query
          </Button>
        </Stack>
        <Stack spacing={1}>
          {queries.map((q: any, idx: number) => (
            <Box
              key={idx}
              sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 0.5 }}
              data-testid={`research-brief-query-${idx}`}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Chip size="small" label={`#${idx + 1}`} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flex: 1, textTransform: 'uppercase' }}
                >
                  {q.expectedSourceType ?? 'news'}
                </Typography>
                <IconButton size="small" onClick={() => handleRemoveQuery(idx)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
              <TextField
                fullWidth
                size="small"
                value={q.text ?? ''}
                placeholder="Query text"
                onChange={(e) => handleQueryChange(idx, 'text', e.target.value)}
                sx={{ mt: 0.5 }}
              />
              <TextField
                fullWidth
                size="small"
                value={q.rationale ?? ''}
                placeholder="Rationale"
                onChange={(e) => handleQueryChange(idx, 'rationale', e.target.value)}
                sx={{ mt: 0.5 }}
              />
            </Box>
          ))}
        </Stack>
      </Box>

      <ChipEditor
        label="Preferred sources (domain allowlist)"
        items={preferredSources}
        onChange={setPreferredSources}
        newValue={newPreferred}
        setNewValue={setNewPreferred}
        placeholder="e.g. gartner.com"
        testid="research-brief-preferred"
      />

      <ChipEditor
        label="Negative sources (domain blocklist)"
        items={negativeSources}
        onChange={setNegativeSources}
        newValue={newNegative}
        setNewValue={setNewNegative}
        placeholder="e.g. contentfarm.net"
        testid="research-brief-negative"
      />

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button
          variant="contained"
          disabled={!dirty || saving}
          onClick={handleSave}
          data-testid="research-brief-save"
        >
          {saving ? 'Saving…' : 'Save brief'}
        </Button>
      </Stack>
    </Stack>
  );
};

interface ChipEditorProps {
  label: string;
  items: string[];
  onChange: (next: string[]) => void;
  newValue: string;
  setNewValue: (v: string) => void;
  placeholder: string;
  testid: string;
}

const ChipEditor: React.FC<ChipEditorProps> = ({
  label, items, onChange, newValue, setNewValue, placeholder, testid,
}) => {
  const handleAdd = () => {
    const v = newValue.trim();
    if (!v || items.includes(v)) return;
    onChange([...items, v]);
    setNewValue('');
  };
  const handleDelete = (v: string) => onChange(items.filter((x) => x !== v));

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
        {items.map((v) => (
          <Chip
            key={v}
            label={v}
            size="small"
            onDelete={() => handleDelete(v)}
            data-testid={`${testid}-chip-${v}`}
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
        <TextField
          size="small"
          placeholder={placeholder}
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); handleAdd(); }
          }}
          sx={{ flex: 1 }}
          data-testid={`${testid}-input`}
        />
        <Button size="small" onClick={handleAdd}>Add</Button>
      </Stack>
    </Box>
  );
};

export default ResearchBriefEditor;
